import { store } from '../../../redux/store';
import { veevaApi } from '../../../redux/RTK/veevaReconcileSlice';
import { API_BASE } from './apiClient';

export function toUpdatedOnString(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (
    typeof value === 'object' &&
    value !== null &&
    'toISOString' in value &&
    typeof value.toISOString === 'function'
  ) {
    return value.toISOString();
  }
  if (typeof value === 'object' && value !== null && 'year' in value) {
    const d = value;
    const y = d.year ?? 0;
    const m = String(d.monthValue ?? d.month ?? 1).padStart(2, '0');
    const day = String(d.dayOfMonth ?? 1).padStart(2, '0');
    const h = String(d.hour ?? 0).padStart(2, '0');
    const min = String(d.minute ?? 0).padStart(2, '0');
    const s = String(d.second ?? 0).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}:${s}`;
  }
  return null;
}

export function toUpdatedByString(value) {
  if (value == null) return null;
  if (value === -1 || value === '-1') return null;
  if (typeof value === 'string') return value.trim() || null;
  return null;
}

function mapSpringToRow(r) {
  return {
    recon_id: r.reconId ?? 0,
    company: r.company ?? null,
    order_num: r.orderNum ?? null,
    sub_order_num: r.subOrderNum ?? null,
    item_number: r.itemNumber ?? null,
    tracking_num: r.trackingNum ?? null,
    hibbert_delivery_date: r.hibbertDeliveryDate ?? null,
    veeva_delivery_date: r.veevaDeliveryDate ?? null,
    hib_aoc_status: r.hibAocStatus ?? null,
    veeva_aoc_status: r.veevaAocStatus ?? null,
    recon_status: r.reconStatus ?? null,
    comments: r.comments ?? null,
    veeva_ord_identifier: r.veevaOrdIdentifier ?? null,
    veeva_aoc_identifer: r.veevaAocIdentifer ?? null,
    updated_by: toUpdatedByString(r.updatedBy),
    updated_on: toUpdatedOnString(r.updatedOn),
    active_flag: r.activeFlag ?? null,
    pcr_num: r.pcrNum ?? null,
    requested_by: r.requestedBy ?? null,
    requested_date:
      r.requestedDate != null
        ? typeof r.requestedDate === 'string'
          ? r.requestedDate
          : toUpdatedOnString(r.requestedDate)
        : null,
    pending_approval: r.pendingApproval ?? null,
  };
}

function filterRows(rows, params) {
  let out = rows;
  if (params.company?.trim()) {
    const c = params.company.trim().toLowerCase();
    out = out.filter((r) => (r.company ?? '').toLowerCase().includes(c));
  }
  if (params.order_num?.trim()) {
    const o = params.order_num.trim().toLowerCase();
    out = out.filter((r) => (r.order_num ?? '').toLowerCase().includes(o));
  }
  if (params.sub_order_num?.trim()) {
    const s = params.sub_order_num.trim().toLowerCase();
    out = out.filter((r) =>
      (r.sub_order_num ?? '').toLowerCase().includes(s)
    );
  }
  if (params.item_number?.trim()) {
    const i = params.item_number.trim().toLowerCase();
    out = out.filter((r) => (r.item_number ?? '').toLowerCase().includes(i));
  }
  if (params.tracking_like?.trim()) {
    const t = params.tracking_like.trim().toLowerCase();
    out = out.filter((r) => (r.tracking_num ?? '').toLowerCase().includes(t));
  }
  if (params.hib_aoc_status?.trim()) {
    const h = params.hib_aoc_status.trim().toLowerCase();
    out = out.filter((r) =>
      (r.hib_aoc_status ?? '').toLowerCase().includes(h)
    );
  }
  if (params.veeva_aoc_status?.trim()) {
    const v = params.veeva_aoc_status.trim().toLowerCase();
    out = out.filter((r) =>
      (r.veeva_aoc_status ?? '').toLowerCase().includes(v)
    );
  }
  if (params.recon_status?.trim()) {
    const s = params.recon_status.trim().toLowerCase();
    out = out.filter((r) => (r.recon_status ?? '').toLowerCase().includes(s));
  }
  if (params.comments_like?.trim()) {
    const c = params.comments_like.trim().toLowerCase();
    out = out.filter((r) => (r.comments ?? '').toLowerCase().includes(c));
  }
  if (params.pcr_num?.trim()) {
    const p = params.pcr_num.trim().toLowerCase();
    out = out.filter((r) => (r.pcr_num ?? '').toLowerCase().includes(p));
  }
  if (params.active_flag !== undefined && params.active_flag !== null) {
    out = out.filter((r) => r.active_flag === 1);
  }
  if (params.updated_from?.trim()) {
    const from = params.updated_from.trim().slice(0, 10);
    out = out.filter(
      (r) => r.updated_on && r.updated_on.slice(0, 10) >= from
    );
  }
  if (params.updated_to?.trim()) {
    const to = params.updated_to.trim().slice(0, 10);
    out = out.filter((r) => r.updated_on && r.updated_on.slice(0, 10) <= to);
  }
  return out;
}

export async function getDistinctCompanies() {
  const promise = store.dispatch(
    veevaApi.endpoints.getReconAudit.initiate({ limit: 500 })
  );
  try {
    const data = await promise.unwrap();
    const rows = Array.isArray(data) ? data : [];
    return Array.from(
      new Set(
        rows
          .map((r) => (r.company ?? '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  } catch {
    return [];
  } finally {
    promise.unsubscribe();
  }
}

async function fetchReconAudit(params) {
  const limit = 500;
  const requestParams = { limit };
  if (params.activeOnly === true) requestParams.activeOnly = true;
  if (params.recon_status?.trim())
    requestParams.recon_status = params.recon_status.trim();
  if (params.order_num?.trim())
    requestParams.order_num = params.order_num.trim();
  if (params.item_number?.trim())
    requestParams.item_number = params.item_number.trim();
  if (params.from_date?.trim())
    requestParams.from_date = params.from_date.trim().slice(0, 10);
  if (params.to_date?.trim())
    requestParams.to_date = params.to_date.trim().slice(0, 10);

  const promise = store.dispatch(
    veevaApi.endpoints.getReconAudit.initiate(requestParams)
  );
  try {
    const data = await promise.unwrap();
    const rawRows = Array.isArray(data) ? data.map(mapSpringToRow) : [];
    const filtered = filterRows(rawRows, params);
    const total = filtered.length;
    const page = Math.max(1, params.page ?? 1);
    const pageSize = Math.max(1, params.page_size ?? 25);
    const start = (page - 1) * pageSize;
    const rows = filtered.slice(start, start + pageSize);
    return { page, page_size: pageSize, total, rows };
  } finally {
    promise.unsubscribe();
  }
}

export async function getIssues(params = {}) {
  try {
    return await fetchReconAudit({ ...params, activeOnly: true });
  } catch (err) {
    const message =
      err?.status === 404
        ? 'Recon audit API not available. Start the backend with Oracle (recon.useMock=false).'
        : err?.data?.message ||
          (err instanceof Error ? err.message : 'Failed to load issues') ||
          `Failed to load issues. Is the backend running at ${API_BASE}?`;
    throw new Error(String(message));
  }
}

export async function getTransactions(params = {}) {
  try {
    const { activeOnly, ...rest } = params;
    return await fetchReconAudit(rest);
  } catch (err) {
    const message =
      err?.status === 404
        ? 'Recon audit API not available. Start the backend with Oracle (recon.useMock=false).'
        : err?.data?.message ||
          (err instanceof Error
            ? err.message
            : 'Failed to load transactions') ||
          `Failed to load transactions. Is the backend running at ${API_BASE}?`;
    throw new Error(String(message));
  }
}

export async function markIgnorePending(reconId, comments) {
  const result = await store.dispatch(
    veevaApi.endpoints.markIgnorePending.initiate({
      reconId,
      comments: comments ?? '',
    })
  ).unwrap();
  return {
    reconId: result.reconId,
    pendingApproval: result.pendingApproval ?? '',
    requestedBy: result.requestedBy ?? null,
    requestedDate: result.requestedDate ?? '',
    comments: result.comments ?? '',
    updatedBy: toUpdatedByString(result.updatedBy) ?? null,
    updatedOn: toUpdatedOnString(result.updatedOn) ?? null,
  };
}

export async function fetchPendingApprovals() {
  const promise = store.dispatch(
    veevaApi.endpoints.getPendingApprovals.initiate()
  );
  try {
    const data = await promise.unwrap();
    return Array.isArray(data) ? data : [];
  } finally {
    promise.unsubscribe();
  }
}

export async function processApproval(reconId, action) {
  return store.dispatch(
    veevaApi.endpoints.processApproval.initiate({ reconId, action })
  ).unwrap();
}

export async function getReconActionAudit() {
  const promise = store.dispatch(
    veevaApi.endpoints.getReconActionAudit.initiate()
  );
  try {
    const data = await promise.unwrap();
    return Array.isArray(data) ? data : [];
  } finally {
    promise.unsubscribe();
  }
}
